import styled, { css, DefaultTheme } from 'styled-components'
import BasePageLayout from 'layouts/PageLayout'

import { breakpoint } from '@sketch/global-styles'

/**
 * TYPES
 */
export interface PageLayoutProps {
  $darkBackground?: boolean
  isSidebarRightOpen?: boolean
}

interface LeftSidebarContainerProps {
  isSidebarLeftOpen?: boolean
}

const getPageLayoutBgColor = ({
  isSidebarRightOpen,
  theme,
}: PageLayoutProps & {
  theme: DefaultTheme
}) => {
  /*
    Use the same `background-color` as the canvas, to avoid the appearance of a
    white trail as the sidebar is being closed (actually the `PageLayout` background
    color "peeking" through a gap between canvas & DS during the animation).
    SEE: https://github.com/sketch-hq/Cloud/issues/13299
  */
  if (!isSidebarRightOpen) return theme.colors.background.secondary.B

  return theme.colors.background.secondary.A
}

export const PageLayout = styled(BasePageLayout)<PageLayoutProps>(
  ({ $darkBackground, theme, isSidebarRightOpen }) => css`
    /* Force this container to have scroll so the sticky objects can be topped correctly */
    flex: 1;
    overflow: auto;

    /* Force the sidebar placeholder to exist next to the content */
    flex-direction: row;
    background-color: ${getPageLayoutBgColor({
      theme,
      isSidebarRightOpen,
    })};

    & ${RightSidebarContainer} {
      background-color: ${!$darkBackground
        ? theme.colors.background.secondary.B
        : theme.colors.background.secondary.A};
    }
  `
)

/**
 * COMPONENTS
 */
const RightSidebarContainer = styled.div(
  ({ theme }) => css`
    display: flex;

    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;

    transition-timing-function: ease-in-out;
    transition-duration: 0.3s;
    transition-property: transform, box-shadow;
    transform: translate(100%, 0);

    box-shadow: 0px 0px 0px 0px transparent;

    z-index: ${theme.zIndex[5] /* on top of header and backdrop */};
    overflow: hidden;

    &[aria-hidden='false'] {
      box-shadow: 0px 0px 0px 1px ${theme.colors.border.A}; /* Emulates a left border */
      transform: translate(0, 0);
    }

    ${breakpoint('base', 'sm')`
      /* We moved to position:fixed here because safari is showing the sidebar even when it's overflow:hidden */
      position: fixed;

      &[aria-hidden='false'] {
        box-shadow: 0px 0px 6px 1px ${theme.colors.border.A};
      }
    `}
  `
)

const SidebarLeftContainer = styled.aside<LeftSidebarContainerProps>(
  ({ theme, isSidebarLeftOpen }) => css`
    width: ${theme.sidebar.leftWidth}px;

    position: fixed;
    top: 0;
    bottom: 0;
    z-index: ${theme.zIndex[4]}; /* bigger z-index than header */

    display: flex;

    background-color: ${theme.colors.background.secondary.B};
    border-right: 1px solid ${theme.colors.border.A};
    border-top-width: 0;
    border-bottom-width: 0;

    box-shadow: ${theme.shadows.boxShadow.elevated};

    will-change: transform; /* Force hardware acceleration */
    transform: ${isSidebarLeftOpen ? 'translateX(0)' : 'translateX(-100%)'};

    transition-timing-function: ease-in-out;
    transition-duration: 0.3s;
    transition-property: transform, opacity;

    opacity: ${({ isSidebarLeftOpen }: LeftSidebarContainerProps) =>
      isSidebarLeftOpen ? 1 : 0};
  `
)

/**
 * This component will allow the sidebar to be inline with the content,
 * to keep it's size.
 *
 * It will allow the animation to run smoother because it's more simpler
 * to draw
 *
 * It's only used in desktop breakpoint
 */
const SidebarPlaceholder = styled.div`
  width: 0;
  transition-timing-function: ease-in-out;
  transition-duration: 0.3s;
  transition-property: width;
  will-change: width;
`

/**
 * This component will show in mobile when the right-sidebar is opened,
 * to allow the user to click on it to close the right-sidebar
 *
 * This component is only rendered in mobile Look at SidebarLayout.tsx
 */
const SidebarBackdrop = styled.div`
  position: fixed;

  top: 0;
  bottom: 0;
  right: 0;
  left: 0;

  z-index: ${({ theme }) => theme.zIndex[4]};

  background: ${({ theme }) => theme.colors.background.tertiary.A};

  transition-timing-function: ease-in-out;
  transition-duration: 0.3s;
  transition-property: background, visibility;

  ${RightSidebarContainer}[aria-hidden="true"] + & {
    background: none;
    /* This forces the backdrop to be hidden/actionable when the sidebar is hidden */
    visibility: hidden;
  }
`

const ContentContainer = styled.div.attrs({
  'data-content-scrollable': true,
  className: 'ContentContainer',
})`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 100%;

  overflow: auto;
`

export {
  ContentContainer,
  RightSidebarContainer,
  SidebarLeftContainer,
  SidebarPlaceholder,
  SidebarBackdrop,
}
